<template>
  <div class="mb-7 flex flex-col space-y-5">
    <span class="font-semibold text-gray-700">{{ docs.name }}</span>
    <lf-description v-if="docs.description">
      {{ docs.description }}
    </lf-description>
    <div>
      <div class="max-h-60 overflow-auto">
        <uploaded-stip-file-preview
          v-for="(doc, index) in uploadedDocs"
          :key="index"
          :index="index"
          :offer-stip-id="docs.id"
          :file="doc"
        />
      </div>
      <div
        class="relative mt-4 max-w-full flex-col border-2 border-dashed border-gray-300 bg-gray-50"
        :class="[percentCompleted || uploadedDocs?.length ? 'py-5' : 'py-10']"
        @drop.prevent="handleDrop"
        @dragover.prevent="dragOver"
        @dragleave.prevent="dragLeave"
      >
        <div
          :class="`absolute inset-y-0 z-30 h-full transition-all ${stateBackgrounds[uploadState]}`"
          :style="`width: ${percentCompleted}%;`"
        />
        <div
          v-show="uploadState === uploadStates.initial"
          class="flex max-w-full flex-col items-center justify-center"
        >
          <div
            class="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100"
          >
            <icon-base width="30" height="30" :icon="IconUploadFile" />
          </div>
          <span
            class="mt-6 flex w-60 flex-col text-center text-sm text-gray-500"
          >
            <span>{{ $t("EXTERNAL.CLOSING_DOCS.UPLOAD_TEXT_1") }}</span>
            <label for="browse" class="cursor-pointer text-primary">
              {{ $t("EXTERNAL.CLOSING_DOCS.UPLOAD_TEXT_LINK") }}
            </label>
            <input
              id="browse"
              type="file"
              multiple
              class="hidden"
              accept=".doc, .pdf, .xls, .xlsx, image/*, .docx, .heic"
              @change="
                (e) => {
                  uploadFile(e.target.files[0]);
                  // set the input value to null because otherwise
                  // onChange won't work next time you select the same file
                  e.target.value = null;
                }
              "
            />
          </span>
        </div>
        <div
          v-show="uploadState === uploadStates.uploading"
          class="z-50 flex justify-between pl-10 text-lg text-gray-600"
        >
          <span>Uploading...</span>
        </div>
      </div>
      <div
        v-if="uploadState === uploadStates.failed"
        class="z-100 mt-2 flex items-center"
      >
        <icon-base
          class="cursor-pointer"
          :icon="IconReload"
          height="24"
          width="24"
          @click="retry"
        />
        <span>Upload failed. Please try again.</span>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, defineAsyncComponent, ref } from "vue";

import { storeToRefs } from "pinia";

import ExternalApiService from "@/services/external";
import useServicingStore from "@/stores/servicing";

import UploadedStipFilePreview from "@/components/external/UploadedStipFilePreview.vue";
import LfDescription from "@/components/ui/text/LfDescription.vue";

const IconReload = defineAsyncComponent(
  () => import("@/components/icons/IconReload.vue")
);
const IconUploadFile = defineAsyncComponent(
  () => import("@/components/icons/deals/IconUploadFile.vue")
);

const uploadStates = {
  initial: "initial",
  failed: "failed",
  uploading: "uploading",
  uploaded: "uploaded",
};

const stateBackgrounds = {
  initial: "bg-white",
  failed: "bg-red-100",
  uploading: "bg-green-100",
  uploaded: "bg-green-100",
};

export default {
  components: {
    LfDescription,
    UploadedStipFilePreview,
  },
  props: {
    docs: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["file-uploaded"],
  setup(props, { emit }) {
    const servicingStore = useServicingStore();
    const { addUploadedFile } = servicingStore;
    const { uploadedFiles, offerId } = storeToRefs(servicingStore);

    const uploadState = ref(uploadStates.initial);
    const percentCompleted = ref(0);

    const uploadedDocs = computed(() => uploadedFiles.value[props.docs.id]);

    const setUploadedFile = (file) => {
      addUploadedFile({
        file,
        section: props.docs.id,
      });
      emit("file-uploaded", { file, section: props.docs.id });
    };

    const uploadFile = async (file) => {
      try {
        uploadState.value = uploadStates.uploading;
        const response = await ExternalApiService.uploadStipFile(
          offerId.value,
          props.docs.id,
          file,
          {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              file.progress = percentCompleted;
              percentCompleted.value = progress;
            },
          }
        );
        uploadState.value = uploadStates.uploaded;
        file["id"] = response.id;
        await addUploadedFile({
          file,
          section: props.docs.id,
        });
        uploadState.value = uploadStates.initial;
        percentCompleted.value = 0;
        emit("file-uploaded");
        return response;
      } catch (error) {
        uploadState.value = uploadStates.failed;
        return error;
      }
    };

    const handleDrop = (event) => {
      const file = event.dataTransfer.files[0];
      uploadFile(file);
    };

    const dragOver = (event) => {
      if (event.currentTarget.classList.contains("bg-gray-100")) {
        event.currentTarget.classList.remove("bg-blue-100");
        event.currentTarget.classList.add("bg-green-100");
      }
    };

    const dragLeave = (event) => {
      event.currentTarget.classList.add("bg-gray-100");
      event.currentTarget.classList.remove("bg-blue-100");
    };

    const retry = () => {
      uploadState.value = uploadStates.initial;
      percentCompleted.value = 0;
    };

    return {
      IconReload,
      IconUploadFile,
      setUploadedFile,
      uploadedDocs,
      uploadState,
      uploadStates,
      uploadFile,
      handleDrop,
      dragOver,
      dragLeave,
      percentCompleted,
      stateBackgrounds,
      retry,
    };
  },
};
</script>
